import React, { useState } from 'react';
import { 
  BookOpen, 
  GraduationCap, 
  Building2, 
  Check, 
  X,
  Brain,
  Search,
  FileText,
  Target,
  ChevronDown,
  ExternalLink
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import {
  peerReviewedSources,
  preprintSources,
  industrySources,
} from '@/data/research-sources';

const conferenceCategories = [
  {
    icon: Brain,
    title: 'AI & Machine Learning',
    conferences: 'NeurIPS',
    description: 'Understanding LLM mechanics and how AI systems select sources.'
  },
  {
    icon: Search,
    title: 'Information Retrieval',
    conferences: 'KDD',
    description: 'Analyzing optimization strategies for generative search engines.'
  },
  {
    icon: FileText,
    title: 'Search & Citation Analysis',
    conferences: 'arXiv preprints',
    description: 'Supporting research from the University of Toronto, Ruhr University and the Max Planck Institute.'
  },
  {
    icon: Target,
    title: 'Why It Matters',
    conferences: null,
    description: 'We translate complex academic findings into practical strategies for brands.'
  }
];

const researchCriteria = {
  prioritize: [
    'Published academic research',
    'Large-scale studies (1,000+ queries)',
    'Official platform documentation',
    'Transparent methodologies',
    'Recent publications (2023-2026)'
  ],
  avoid: [
    'Anecdotal evidence',
    'Unverified claims',
    'Marketing materials',
    'Small sample sizes (<100)',
    'Outdated studies (pre-2023)'
  ]
};

// Source Card Component
import type { AcademicSource } from '@/data/research-sources';

interface SourceCardProps {
  source: AcademicSource;
  isPeerReviewed: boolean;
}

const SourceCard = ({ source, isPeerReviewed }: SourceCardProps) => (
  <a
    href={source.url}
    target="_blank"
    rel="noopener noreferrer"
    className="flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors group border-b border-border last:border-b-0"
  >
    <div className="space-y-1">
      <p className="font-medium text-foreground group-hover:text-primary transition-colors">
        {source.title}
      </p>
      <p className="text-xs text-muted-foreground">
        {source.authors}, {source.venue}
        {source.institution && ` (${source.institution})`}
      </p>
      <p className="text-xs text-muted-foreground italic">"{source.finding}"</p>
    </div>
    <div className="flex items-center gap-2 shrink-0 ml-4">
      {isPeerReviewed ? (
        <span className="text-xs font-medium text-success bg-success/10 px-2 py-0.5 rounded">
          Peer-Reviewed <Check className="inline w-3 h-3" />
        </span>
      ) : (
        <span className="text-xs font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded">
          Preprint
        </span>
      )}
      <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
    </div>
  </a>
);

// Main Component
export const ResearchFoundation = () => {
  const [industryOpen, setIndustryOpen] = useState(false);

  return (
    <section className="space-y-8" id="research-foundation">
      {/* Section Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-primary rounded-lg">
          <BookOpen className="w-6 h-6 text-primary-foreground" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-foreground">Research Foundation</h2>
          <p className="text-sm text-muted-foreground">Built on academic research, not guesswork</p>
        </div>
      </div>

      {/* Intro Paragraph */}
      <p className="text-muted-foreground leading-relaxed">
        Unlike traditional SEO approaches often based on trial-and-error, our methodology is grounded in{' '}
        <strong className="text-foreground">rigorous empirical research</strong>. Our models and recommendations are derived from
        peer-reviewed papers and supporting preprints from leading research institutions (2024-2025).
      </p>

      {/* Conference Categories Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {conferenceCategories.map((category) => {
          const IconComponent = category.icon;
          return (
            <Card key={category.title} className="bg-muted/30">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3 mb-2">
                  <IconComponent className="w-5 h-5 text-primary" />
                  <h4 className="font-semibold text-foreground">{category.title}</h4>
                </div>
                <p className="text-sm text-muted-foreground">
                  {category.conferences && (
                    <strong className="text-foreground">{category.conferences}:</strong>
                  )}{' '}
                  {category.description}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Academic Sources */}
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <GraduationCap className="w-5 h-5 text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Academic Sources</h3>
          <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded">
            Primary
          </span>
        </div>

        {/* Peer-Reviewed Papers */}
        <Card>
          <CardContent className="p-0">
            <div className="px-4 py-3 bg-muted/50 border-b border-border flex items-center justify-between">
              <span className="font-medium text-foreground">Peer-Reviewed Research</span>
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Check className="w-3 h-3 text-success" />
                Verified at major conferences
              </span>
            </div>
            <div>
              {peerReviewedSources.map((source) => (
                <SourceCard key={source.id} source={source} isPeerReviewed={true} />
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Preprints */}
        <Card>
          <CardContent className="p-0">
            <div className="px-4 py-3 bg-muted/50 border-b border-border flex items-center justify-between">
              <span className="font-medium text-foreground">arXiv Preprints</span>
              <span className="text-xs text-muted-foreground">From recognized institutions</span>
            </div>
            <div>
              {preprintSources.map((source) => (
                <SourceCard key={source.id} source={source} isPeerReviewed={false} />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Research Criteria */}
      <Card>
        <CardContent className="pt-6">
          <h4 className="font-semibold text-foreground mb-4">Our Research Criteria</h4>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h5 className="font-medium text-success mb-3 flex items-center gap-2">
                <Check className="w-4 h-4" />
                We prioritize:
              </h5>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {researchCriteria.prioritize.map((item, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-success shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h5 className="font-medium text-destructive mb-3 flex items-center gap-2">
                <X className="w-4 h-4" />
                We avoid:
              </h5>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {researchCriteria.avoid.map((item, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <X className="w-4 h-4 text-destructive shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Industry Studies (Collapsible) */}
      <Card>
        <CardContent className="p-0">
          <button
            onClick={() => setIndustryOpen(!industryOpen)}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors"
            aria-expanded={industryOpen}
          >
            <div className="flex items-center gap-3">
              <Building2 className="w-5 h-5 text-muted-foreground" />
              <span className="font-medium text-foreground">Industry Studies</span>
              <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
                Further Reading
              </span>
            </div>
            <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${industryOpen ? 'rotate-180' : ''}`} />
          </button>
          
          {industryOpen && (
            <div className="px-4 pb-4 border-t border-border">
              <p className="text-xs text-muted-foreground py-3">
                These industry reports provide additional context and practical data. 
                Used for supplementary insights only, not for statistical claims.
              </p>
              <div className="space-y-2">
                {industrySources.map((source) => (
                  <a
                    key={source.id}
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors group"
                  >
                    <div>
                      <p className="font-medium text-foreground group-hover:text-primary transition-colors text-sm">
                        {source.title}
                      </p>
                      <p className="text-xs text-muted-foreground">{source.description}</p>
                    </div>
                    <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                  </a>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Footer Note */}
      <p className="text-xs text-muted-foreground">
        Sources from Princeton, TU Darmstadt, NAVER AI, Northeastern University, the University of Toronto,
        Ruhr University and the Max Planck Institute.
        Papers marked "Peer-Reviewed" have undergone formal review at major conferences (KDD, NeurIPS).
      </p>
    </section>
  );
};

export default ResearchFoundation;
